// Keeps a screen's own target centred while the page settles (#1655).
//
// A screen that opens a fold and centres a card (SettingsScreen) cannot trust one
// `scrollIntoView({ block: "center" })`: on the first frame the page is still growing, so
// the scroll is clamped short. This centres on the next frame and re-centres on a layout
// change for a bounded window (KEEP_CENTRED_MS), under the same rule as the #hash follow
// in ScrollToTop (D452): it remembers scrollY after every scroll it makes itself, and stops
// the moment the page is anywhere else (a script, find-in-page, focus, a hash move). It also
// stops at once on the first wheel, touch, key or mouse press, and when the window ends.
//
// `find` returns the element to centre. Returns `stop`, which is also the cleanup.
export const KEEP_CENTRED_MS = 2000;
const DRIFT_PX = 1;
const USER_INPUT_EVENTS = ["wheel", "touchstart", "touchmove", "keydown", "mousedown"];

// How far the element's margin box is from where `scrollIntoView({ block: "center" })` puts it: the
// scrollport's centre, scroll-padding and scroll-margin respected (not the window's middle).
export function centreOffset(el) {
  const root = window.getComputedStyle(document.documentElement);
  const own = window.getComputedStyle(el);
  const px = (v) => parseFloat(v) || 0;
  const r = el.getBoundingClientRect();
  const boxMid = (r.top - px(own.scrollMarginTop) + r.bottom + px(own.scrollMarginBottom)) / 2;
  const portMid = (px(root.scrollPaddingTop) + window.innerHeight - px(root.scrollPaddingBottom)) / 2;
  return boxMid - portMid;
}

export function keepCentred(find) {
  let stopped = false;
  let lastY = null;
  const cleanups = [];
  const stop = () => {
    stopped = true;
    cleanups.forEach((fn) => fn());
    cleanups.length = 0;
  };

  const centre = () => {
    find()?.scrollIntoView?.({ block: "center" });
    lastY = window.scrollY;
  };

  const onLayoutChange = () => {
    if (stopped) return;
    if (Math.abs(window.scrollY - lastY) > DRIFT_PX) {
      stop(); // someone else moved the page
      return;
    }
    const el = find();
    if (el && Math.abs(centreOffset(el)) > DRIFT_PX) centre();
  };

  const raf = window.requestAnimationFrame(() => {
    centre();
    if (typeof ResizeObserver === "function") {
      const resize = new ResizeObserver(onLayoutChange);
      resize.observe(document.body);
      cleanups.push(() => resize.disconnect());
    }
  });
  cleanups.push(() => window.cancelAnimationFrame(raf));
  USER_INPUT_EVENTS.forEach((type) => {
    window.addEventListener(type, stop, { passive: true, once: true });
    cleanups.push(() => window.removeEventListener(type, stop));
  });
  const timer = window.setTimeout(stop, KEEP_CENTRED_MS);
  cleanups.push(() => window.clearTimeout(timer));
  return stop;
}
